import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useEventCallback } from '../../../ui/hooks';
import { isNull, isObject } from '../../../ui/utils';
import type { FilterControlProps, FilterOption } from '../types';
import { FilterGroup } from './SelectControls';

type SwitchOptions = { on?: FilterOption; off?: FilterOption };

export function SwitchFilterControl({ field, value: valueProp, setQuery, active }: FilterControlProps) {
  const id = useId();
  const options = (isObject(field.options) ? field.options : {}) as SwitchOptions;
  const value = useMemo(() => {
    if (isNull(valueProp)) {
      if (!isNull(options.on) || !isNull(options.off)) {
        return isNull(options.on) ? true : false;
      }
    }
    return valueProp;
  }, [valueProp, options.on, options.off]);
  const labels = useMemo(
    () => ({
      on: isObject(options.on) && !isNull(options.on?.label) ? `${options.on?.label}` : undefined,
      off: isObject(options.off) && !isNull(options.off?.label) ? `${options.off?.label}` : undefined,
    }),
    [options.on, options.off]
  );

  const [checked, setChecked] = useState(value ? true : false);
  const checkRef = useRef(checked);
  checkRef.current = checked;
  useEffect(() => {
    const val = value ? true : false;
    if (val !== checkRef.current) {
      setChecked((curr) => (curr !== val ? val : curr));
    }
  }, [value]);

  const onCheckedChange = useEventCallback((next: boolean) => {
    setChecked(next);
    if (field.setField) {
      field.setField(next, setQuery, field);
    }
  });

  const label = checked ? labels.on : labels.off;
  return (
    <FilterGroup field={field} active={active}>
      <div className="flex items-center gap-2">
        <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} aria-label={label ? undefined : field.label} />
        {label ? <Label htmlFor={id}>{label}</Label> : null}
      </div>
    </FilterGroup>
  );
}

export const CheckboxFilterControl = SwitchFilterControl;
